import { useState, useEffect, useCallback } from 'react';

import { useApp } from '@/hooks/use-app-context';
import { languageApi } from '@/api/language.api';
import type { Language } from '@/lib/types/language';

type LanguageScope = 'all' | 'mine';

interface UseLanguagesOptions {
  scope?: LanguageScope;
  enabled?: boolean;
}

interface UseLanguagesReturn {
  languages: Language[];
  activeLanguages: Language[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const languageCache = new Map<string, { data: Language[]; timestamp: number }>();
const LOCAL_STORAGE_PREFIX = 'yodi:languages';
const LANGUAGE_UPDATE_EVENT = 'languages:updated';

const getCacheKey = (scope: LanguageScope, userId?: string) =>
  scope === 'mine' ? `${LOCAL_STORAGE_PREFIX}:mine:${userId ?? 'anonymous'}` : `${LOCAL_STORAGE_PREFIX}:all`;

export const invalidateLanguageCaches = () => {
  languageCache.clear();

  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(LOCAL_STORAGE_PREFIX))
      .forEach((key) => localStorage.removeItem(key));

    localStorage.setItem(LANGUAGE_UPDATE_EVENT, String(Date.now()));
  } catch {
    // ignore localStorage errors
  }

  window.dispatchEvent(new CustomEvent(LANGUAGE_UPDATE_EVENT));
};

export function useLanguages(options: UseLanguagesOptions = {}): UseLanguagesReturn {
  const { scope = 'all', enabled = true } = options;
  const { user } = useApp();
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const cacheKey = getCacheKey(scope, user?.id);

  const fetchLanguages = useCallback(async (forceRefresh = false) => {
    if (!enabled) {
      setLanguages([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    if (scope === 'mine' && !user?.id) {
      setLanguages([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    try {
      // Use cache if available and fresh
      const now = Date.now();
      const cachedEntry = languageCache.get(cacheKey);
      if (!forceRefresh && cachedEntry && now - cachedEntry.timestamp < CACHE_DURATION) {
        setLanguages(cachedEntry.data);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      const data = await (scope === 'mine' ? languageApi.getMyLanguages() : languageApi.getLanguages());
      if (Array.isArray(data)) {
        languageCache.set(cacheKey, { data, timestamp: now });
        setLanguages(data);
        try {
          localStorage.setItem(cacheKey, JSON.stringify(data));
        } catch {
          // ignore localStorage errors
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement des langues';
      setError(message);
      console.error('Failed to fetch languages:', err);
      // If fetch fails, attempt to restore from localStorage fallback
      try {
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const stored = JSON.parse(raw) as Language[];
          if (Array.isArray(stored) && stored.length) {
            setLanguages(stored);
          }
        }
      } catch {
        // ignore parse errors
      }
    } finally {
      setIsLoading(false);
    }
  }, [cacheKey, enabled, scope, user?.id]);

  useEffect(() => {
    if (!enabled) {
      setLanguages([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Initialize from localStorage synchronously for persistence, then refresh from API
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const stored = JSON.parse(raw) as Language[];
        if (Array.isArray(stored) && stored.length) {
          setLanguages(stored);
          setIsLoading(false);
        }
      }
    } catch {
      // ignore
    }
    void fetchLanguages();
  }, [cacheKey, enabled, fetchLanguages]);

  // Listen to cross-tab/local updates to invalidate cache
  useEffect(() => {
    const handleStorageUpdate = (e: StorageEvent) => {
      if (e.key === LANGUAGE_UPDATE_EVENT) {
        // Invalidate module cache and refetch
        languageCache.delete(cacheKey);
        void fetchLanguages(true);
      }
    };

    const handleLocalUpdate = () => {
      languageCache.delete(cacheKey);
      void fetchLanguages(true);
    };

    window.addEventListener('storage', handleStorageUpdate);
    window.addEventListener(LANGUAGE_UPDATE_EVENT, handleLocalUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageUpdate);
      window.removeEventListener(LANGUAGE_UPDATE_EVENT, handleLocalUpdate);
    };
  }, [cacheKey, fetchLanguages]);

  const activeLanguages = languages.filter(
    (lang) => lang.active && (lang.approvalStatus === undefined || lang.approvalStatus === 'approved')
  );

  const refetch = useCallback(() => fetchLanguages(true), [fetchLanguages]);

  return {
    languages,
    activeLanguages,
    isLoading,
    error,
    refetch,
  };
}

